"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  RotateCcw,
  Search,
  SlidersHorizontal,
  Sparkles,
  X,
} from "lucide-react";
import { CustomSelect, type CustomSelectOption } from "@/components/ui/custom-select";
import { API_URL } from "@/lib/api/client";
import { companyBrandFont } from "@/lib/jobs/fonts";
import {
  EXPERIENCE_LEVELS,
  QA_CATEGORIES,
  WORKPLACE_TYPES,
  type JobSortOption,
  type QaJobFilters,
  type QaJobsFacets,
  type QaJobsPageResult,
  type QaJobSummary,
} from "@/lib/jobs/types";
import { buildJobsHref, formatAtsName } from "@/lib/jobs/utils";
import { cn } from "@/lib/utils";
import { JobCard } from "./job-card";

const QUICK_TECH_TAGS = [
  "Playwright",
  "Selenium",
  "Cypress",
  "API Testing",
  "TypeScript",
  "Java",
  "Python",
  "SQL",
  "JMeter",
  "Appium",
] as const;

const MAX_EXP_OPTIONS: ReadonlyArray<{ label: string; value: number | null }> = [
  { label: "Any", value: null },
  { label: "≤ 2 yrs", value: 2 },
  { label: "≤ 5 yrs", value: 5 },
  { label: "≤ 8 yrs", value: 8 },
];

const SORT_SELECT_OPTIONS: CustomSelectOption[] = [
  { value: "newest", label: "Sort: Newest Verified" },
  { value: "enterprise", label: "Sort: Top Enterprises" },
  { value: "exp_asc", label: "Sort: Entry-Friendly First" },
];

const POPULAR_SEO_SEARCHES: ReadonlyArray<{ label: string; subtitle: string; href: string }> = [
  {
    label: "Remote QA & SDET Jobs",
    subtitle: "Work-from-anywhere automation & manual roles",
    href: "/qa-jobs?workplace=Remote",
  },
  {
    label: "QA Jobs in India",
    subtitle: "Bengaluru, Hyderabad, Pune, Chennai & remote",
    href: "/qa-jobs?country=India",
  },
  {
    label: "QA Jobs in United States",
    subtitle: "US remote, hybrid & enterprise SDET openings",
    href: "/qa-jobs?country=United+States",
  },
  {
    label: "Entry-Level QA Jobs (0–2 Yrs)",
    subtitle: "Junior QA & graduate automation engineer roles",
    href: "/qa-jobs?maxExp=2",
  },
  {
    label: "SDET & Test Automation Jobs",
    subtitle: "Framework engineering, CI/CD & E2E automation",
    href: "/qa-jobs?category=SDET+%2F+Test+Automation",
  },
  {
    label: "Playwright Automation Jobs",
    subtitle: "TypeScript, Python & modern browser testing",
    href: "/qa-jobs?q=Playwright",
  },
  {
    label: "Selenium WebDriver Jobs",
    subtitle: "Java, Python & C# enterprise test suites",
    href: "/qa-jobs?q=Selenium",
  },
  {
    label: "API & Performance QA Jobs",
    subtitle: "REST Assured, Postman, JMeter & k6 load testing",
    href: "/qa-jobs?category=API+%26+Integration+Testing",
  },
];

function buildPageNumbers(current: number, total: number): Array<number | "ellipsis"> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, idx) => idx + 1);
  }
  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);

  const output: Array<number | "ellipsis"> = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      output.push("ellipsis");
    }
    output.push(sorted[i]);
  }
  return output;
}

export function JobsExplorer({ result }: { result: QaJobsPageResult }) {
  // Direct client state initialized with SSR data
  const [jobs, setJobs] = useState<QaJobSummary[]>(result.jobs);
  const [totalMatching, setTotalMatching] = useState<number>(result.totalMatching);
  const [page, setPage] = useState<number>(result.page);
  const pageSize = result.pageSize || 20;
  const [totalPages, setTotalPages] = useState<number>(result.totalPages);
  const [filters, setFilters] = useState<QaJobFilters>(result.filters);
  const [facets, setFacets] = useState<QaJobsFacets>(result.facets);

  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState(result.filters.q);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultsTopRef = useRef<HTMLDivElement | null>(null);

  // Initial state is directly hydrated from SSR result prop

  /**
   * Fetches job listings directly from be-target-qa backend over HTTP.
   * Updates browser history via window.history.pushState/replaceState
   * to avoid triggering Next.js Cloudflare Worker _rsc navigation requests.
   */
  const fetchJobs = useCallback(
    async (
      targetFilters: QaJobFilters,
      options?: { replaceUrl?: boolean; skipHistory?: boolean; scrollToTop?: boolean },
    ) => {
      // Abort any pending in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsFetching(true);
      setFetchError(null);
      setFilters(targetFilters);

      // Keep URL query in sync for sharing / bookmarking without calling Next.js router
      const nextHref = buildJobsHref(targetFilters);
      if (!options?.skipHistory && typeof window !== "undefined") {
        if (options?.replaceUrl) {
          window.history.replaceState({ filters: targetFilters }, "", nextHref);
        } else {
          window.history.pushState({ filters: targetFilters }, "", nextHref);
        }
      }

      if (options?.scrollToTop && resultsTopRef.current) {
        resultsTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      try {
        const qp = new URLSearchParams();
        if (targetFilters.q.trim()) qp.set("q", targetFilters.q.trim());
        if (targetFilters.country.trim()) qp.set("country", targetFilters.country.trim());
        if (targetFilters.category.trim()) qp.set("category", targetFilters.category.trim());
        if (targetFilters.level.trim()) qp.set("level", targetFilters.level.trim());
        if (targetFilters.workplace.trim()) qp.set("workplace", targetFilters.workplace.trim());
        if (targetFilters.maxExp !== null && targetFilters.maxExp >= 0) {
          qp.set("maxExp", String(targetFilters.maxExp));
        }
        if (targetFilters.ats.trim()) qp.set("ats", targetFilters.ats.trim());
        if (targetFilters.sort && targetFilters.sort !== "newest") {
          qp.set("sort", targetFilters.sort);
        }
        qp.set("page", String(targetFilters.page));
        qp.set("pageSize", String(pageSize));

        const endpoint = `${API_URL}/api/v1/jobs?${qp.toString()}`;
        const res = await fetch(endpoint, {
          signal: abortController.signal,
          headers: {
            Accept: "application/json",
          },
        });

        if (!res.ok) {
          throw new Error(`Jobs API returned HTTP ${res.status}`);
        }

        const data = (await res.json()) as QaJobsPageResult;
        setJobs(data.jobs || []);
        setTotalMatching(data.totalMatching ?? 0);
        setPage(data.page ?? targetFilters.page);
        setTotalPages(data.totalPages ?? 1);
        if (data.facets && data.facets.totalActiveJobs > 0) {
          setFacets(data.facets);
        }
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }
        console.error("[qa-jobs] Direct client jobs fetch error:", err);
        setFetchError("Unable to update jobs list. Please check your network connection.");
      } finally {
        if (abortControllerRef.current === abortController) {
          setIsFetching(false);
        }
      }
    },
    [pageSize],
  );

  // Listen for browser Back/Forward navigation (popstate)
  useEffect(() => {
    const handlePopState = () => {
      if (typeof window === "undefined") return;
      const sp = new URLSearchParams(window.location.search);
      const poppedFilters: QaJobFilters = {
        q: sp.get("q") ?? "",
        country: sp.get("country") ?? "",
        category: sp.get("category") ?? "",
        level: sp.get("level") ?? "",
        workplace: sp.get("workplace") ?? "",
        maxExp: sp.get("maxExp") ? Number(sp.get("maxExp")) : null,
        ats: sp.get("ats") ?? "",
        sort: (sp.get("sort") as JobSortOption) || "newest",
        page: sp.get("page") ? Math.max(1, parseInt(sp.get("page")!, 10) || 1) : 1,
      };
      setSearchInput(poppedFilters.q);
      fetchJobs(poppedFilters, { skipHistory: true });
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchJobs]);

  const navigateWith = useCallback(
    (
      overrides: Partial<QaJobFilters>,
      options?: { replaceUrl?: boolean; scrollToTop?: boolean },
    ) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      const nextFilters: QaJobFilters = {
        ...filters,
        ...overrides,
        page: overrides.page ?? 1,
      };
      fetchJobs(nextFilters, options);
    },
    [fetchJobs, filters],
  );

  const resetAllFilters = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setSearchInput("");
    const cleanFilters: QaJobFilters = {
      q: "",
      country: "",
      category: "",
      level: "",
      workplace: "",
      maxExp: null,
      ats: "",
      sort: "newest",
      page: 1,
    };
    fetchJobs(cleanFilters, { scrollToTop: true });
  }, [fetchJobs]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      navigateWith({ q: value, page: 1 }, { replaceUrl: true });
    }, 320);
  };

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    navigateWith({ q: searchInput, page: 1 });
  };

  const countryOptions = useMemo<CustomSelectOption[]>(() => {
    const opts: CustomSelectOption[] = [
      { value: "", label: `All Countries (${facets.totalActiveJobs.toLocaleString("en-US")})` },
    ];
    const seen = new Set<string>();
    for (const item of facets.countries) {
      seen.add(item.value);
      opts.push({
        value: item.value,
        label: `${item.value} (${item.count.toLocaleString("en-US")})`,
      });
    }
    if (filters.country && !seen.has(filters.country)) {
      opts.push({ value: filters.country, label: filters.country });
    }
    return opts;
  }, [facets.countries, facets.totalActiveJobs, filters.country]);

  const categoryCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of facets.categories) map.set(c.value, c.count);
    return map;
  }, [facets.categories]);

  const levelCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const l of facets.experienceLevels) map.set(l.value, l.count);
    return map;
  }, [facets.experienceLevels]);

  const activeFilterChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      overrides: Partial<QaJobFilters>;
      clearSearch?: boolean;
    }> = [];
    if (filters.q) {
      chips.push({
        key: `q:${filters.q}`,
        label: `Keyword: "${filters.q}"`,
        overrides: { q: "" },
        clearSearch: true,
      });
    }
    if (filters.country) {
      chips.push({
        key: `country:${filters.country}`,
        label: `Country: ${filters.country}`,
        overrides: { country: "" },
      });
    }
    if (filters.workplace) {
      chips.push({
        key: `workplace:${filters.workplace}`,
        label: `Workplace: ${filters.workplace}`,
        overrides: { workplace: "" },
      });
    }
    if (filters.category) {
      chips.push({
        key: `category:${filters.category}`,
        label: filters.category,
        overrides: { category: "" },
      });
    }
    if (filters.level) {
      chips.push({
        key: `level:${filters.level}`,
        label: `Level: ${filters.level}`,
        overrides: { level: "" },
      });
    }
    if (filters.maxExp !== null) {
      chips.push({
        key: `maxExp:${filters.maxExp}`,
        label: `Max Required: ≤ ${filters.maxExp} yrs`,
        overrides: { maxExp: null },
      });
    }
    if (filters.ats) {
      chips.push({
        key: `ats:${filters.ats}`,
        label: `Portal: ${formatAtsName(filters.ats)}`,
        overrides: { ats: "" },
      });
    }
    return chips;
  }, [filters]);

  const sidebarFilterCount =
    (filters.category ? 1 : 0) +
    (filters.level ? 1 : 0) +
    (filters.maxExp !== null ? 1 : 0) +
    (filters.ats ? 1 : 0);

  const rangeStart = totalMatching === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(totalMatching, page * pageSize);
  const pageItems = buildPageNumbers(page, totalPages);

  const renderSidebarControls = (closeOnPick = false) => {
    const afterPick = () => {
      if (closeOnPick) setMobileDrawerOpen(false);
    };

    return (
      <div className="space-y-6">
        {/* QA Specialization */}
        <div>
          <div className="flex items-center justify-between">
            <h3 className="text-[11px] font-bold uppercase tracking-[.12em] text-muted">
              QA Specialization
            </h3>
            {filters.category && (
              <button
                type="button"
                onClick={() => {
                  navigateWith({ category: "" }, { scrollToTop: true });
                  afterPick();
                }}
                className="text-[11px] font-semibold text-green hover:underline"
              >
                Clear
              </button>
            )}
          </div>
          <div className="mt-2.5 flex flex-col gap-1">
            <button
              type="button"
              onClick={() => {
                navigateWith({ category: "" }, { scrollToTop: true });
                afterPick();
              }}
              className={cn(
                "flex items-center justify-between rounded-[9px] px-2.5 py-2 text-left text-[13px] transition",
                !filters.category
                  ? "bg-[#e8fedf] font-semibold text-[#07533d]"
                  : "text-ink/80 hover:bg-wash",
              )}
            >
              <span>All QA Categories</span>
              <span className="text-[11px] tabular-nums text-muted">
                {facets.totalActiveJobs.toLocaleString("en-US")}
              </span>
            </button>
            {QA_CATEGORIES.map((cat) => {
              const active = filters.category === cat;
              const count = categoryCounts.get(cat) ?? 0;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => {
                    navigateWith({ category: active ? "" : cat }, { scrollToTop: true });
                    afterPick();
                  }}
                  className={cn(
                    "flex items-center justify-between gap-2 rounded-[9px] px-2.5 py-2 text-left text-[13px] transition",
                    active
                      ? "bg-[#e8fedf] font-semibold text-[#07533d]"
                      : "text-ink/80 hover:bg-wash",
                  )}
                >
                  <span className="truncate">{cat}</span>
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-1.5 py-0.5 text-[11px] tabular-nums",
                      active ? "bg-[#0a7653]/15 text-[#07533d]" : "text-muted",
                    )}
                  >
                    {count.toLocaleString("en-US")}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Max Years Required */}
        <div className="border-t border-line pt-5">
          <div className="flex items-center justify-between">
            <h3 className="text-[11px] font-bold uppercase tracking-[.12em] text-muted">
              Max Experience Required
            </h3>
          </div>
          <p className="mt-1 text-[11.5px] leading-4 text-muted">
            Includes roles asking up to your years of experience.
          </p>
          <div className="mt-2.5 grid grid-cols-2 gap-1.5">
            {MAX_EXP_OPTIONS.map((opt) => {
              const active = filters.maxExp === opt.value;
              return (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => {
                    navigateWith({ maxExp: opt.value }, { scrollToTop: true });
                    afterPick();
                  }}
                  className={cn(
                    "h-8.5 rounded-[8px] border px-2.5 text-xs font-semibold transition",
                    active
                      ? "border-[#0a7653] bg-[#0a7653] text-white shadow-xs"
                      : "border-line bg-white text-ink/80 hover:border-ink/30 hover:bg-wash",
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Seniority / Experience Level */}
        <div className="border-t border-line pt-5">
          <div className="flex items-center justify-between">
            <h3 className="text-[11px] font-bold uppercase tracking-[.12em] text-muted">
              Seniority Band
            </h3>
            {filters.level && (
              <button
                type="button"
                onClick={() => {
                  navigateWith({ level: "" }, { scrollToTop: true });
                  afterPick();
                }}
                className="text-[11px] font-semibold text-green hover:underline"
              >
                Clear
              </button>
            )}
          </div>
          <div className="mt-2.5 flex flex-col gap-1">
            {EXPERIENCE_LEVELS.map((lvl) => {
              const active = filters.level === lvl;
              const count = levelCounts.get(lvl) ?? 0;
              return (
                <button
                  key={lvl}
                  type="button"
                  onClick={() => {
                    navigateWith({ level: active ? "" : lvl }, { scrollToTop: true });
                    afterPick();
                  }}
                  className={cn(
                    "flex items-center justify-between gap-2 rounded-[9px] px-2.5 py-1.5 text-left text-[13px] transition",
                    active
                      ? "bg-[#e8fedf] font-semibold text-[#07533d]"
                      : "text-ink/80 hover:bg-wash",
                  )}
                >
                  <span className="truncate">{lvl}</span>
                  <span className="shrink-0 text-[11px] tabular-nums text-muted">
                    {count.toLocaleString("en-US")}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ATS / Hiring Portal */}
        {facets.atsTypes.length > 0 && (
          <div className="border-t border-line pt-5">
            <div className="flex items-center justify-between">
              <h3 className="text-[11px] font-bold uppercase tracking-[.12em] text-muted">
                Employer ATS Portal
              </h3>
              {filters.ats && (
                <button
                  type="button"
                  onClick={() => {
                    navigateWith({ ats: "" }, { scrollToTop: true });
                    afterPick();
                  }}
                  className="text-[11px] font-semibold text-green hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {facets.atsTypes.slice(0, 10).map((ats) => {
                const active = filters.ats === ats.value;
                return (
                  <button
                    key={ats.value}
                    type="button"
                    onClick={() => {
                      navigateWith({ ats: active ? "" : ats.value }, { scrollToTop: true });
                      afterPick();
                    }}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11.5px] font-semibold transition",
                      active
                        ? "border-[#0a7653] bg-[#e8fedf] text-[#07533d]"
                        : "border-line bg-white text-ink/75 hover:border-ink/30 hover:bg-wash",
                    )}
                  >
                    <span>{formatAtsName(ats.value)}</span>
                    <span className="text-[10.5px] opacity-65">{ats.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 sm:px-6 lg:px-10 lg:py-10">
      {/* Top Interactive Search & Primary Filter Bar */}
      <div className="rounded-[18px] border border-[#dfe3e8] bg-white p-4 shadow-[0_8px_28px_rgba(15,23,42,0.04)] sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          {/* Keyword / Title / Company / Skill Search */}
          <form onSubmit={handleSearchSubmit} className="relative min-w-0 flex-1">
            <Search
              size={17}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search title, company, or stack (e.g. Playwright, SDET, Amazon, API)..."
              aria-label="Search QA jobs by role, company, or technology"
              className="h-11 w-full rounded-[11px] border border-line bg-wash/60 pl-10 pr-9 text-[14px] text-ink outline-none transition placeholder:text-muted/75 focus:border-green focus:bg-white focus:ring-2 focus:ring-green/15"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  navigateWith({ q: "" });
                }}
                aria-label="Clear search query"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted hover:bg-black/5 hover:text-ink"
              >
                <X size={15} />
              </button>
            )}
          </form>

          {/* Country + Workplace + Sort + Mobile Filters */}
          <div className="flex flex-wrap items-center gap-2.5">
            <CustomSelect
              label="Filter by country"
              variant="light"
              value={filters.country}
              options={countryOptions}
              onChange={(country) => navigateWith({ country })}
              className="h-11 min-w-[195px] rounded-[11px] text-[13px]"
              menuClassName="min-w-[230px]"
            />

            {/* Workplace Mode Segmented Control */}
            <div
              role="group"
              aria-label="Workplace mode"
              className="inline-flex h-11 items-center rounded-[11px] border border-line bg-wash/75 p-1"
            >
              <button
                type="button"
                onClick={() => navigateWith({ workplace: "" })}
                className={cn(
                  "h-full rounded-[8px] px-3 text-xs font-semibold transition",
                  !filters.workplace
                    ? "bg-white text-ink shadow-xs"
                    : "text-muted hover:text-ink",
                )}
              >
                All
              </button>
              {WORKPLACE_TYPES.map((wp) => {
                const active = filters.workplace === wp;
                return (
                  <button
                    key={wp}
                    type="button"
                    onClick={() => navigateWith({ workplace: active ? "" : wp })}
                    className={cn(
                      "h-full rounded-[8px] px-3 text-xs font-semibold transition",
                      active
                        ? "bg-[#0a7653] text-white shadow-xs"
                        : "text-muted hover:text-ink",
                    )}
                  >
                    {wp}
                  </button>
                );
              })}
            </div>

            <CustomSelect
              label="Sort jobs"
              variant="light"
              align="right"
              value={filters.sort}
              options={SORT_SELECT_OPTIONS}
              onChange={(val) => navigateWith({ sort: val as JobSortOption })}
              className="h-11 min-w-[190px] rounded-[11px] text-[13px]"
              menuClassName="min-w-[210px]"
            />

            <button
              type="button"
              onClick={() => setMobileDrawerOpen(true)}
              className="inline-flex h-11 items-center gap-2 rounded-[11px] border border-line bg-white px-3.5 text-[13px] font-semibold text-ink transition hover:bg-wash lg:hidden"
            >
              <SlidersHorizontal size={15} className="text-green" aria-hidden="true" />
              <span>Filters</span>
              {sidebarFilterCount > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-ink px-1.5 text-[11px] font-bold text-lime">
                  {sidebarFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Quick Tech Stack Filter Strip */}
        <div className="mt-3.5 flex flex-wrap items-center gap-1.5 border-t border-line/70 pt-3">
          <span className="mr-1 inline-flex items-center gap-1 text-[11.5px] font-semibold text-muted">
            <Sparkles size={12} className="text-green" aria-hidden="true" />
            Quick stack:
          </span>
          {QUICK_TECH_TAGS.map((tag) => {
            const active = filters.q.trim().toLowerCase() === tag.toLowerCase();
            return (
              <button
                key={tag}
                type="button"
                onClick={() => {
                  const nextQ = active ? "" : tag;
                  setSearchInput(nextQ);
                  navigateWith({ q: nextQ, page: 1 });
                }}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-[11.5px] font-semibold transition",
                  active
                    ? "border-[#0a7653] bg-[#0a7653] text-white"
                    : "border-line bg-wash/55 text-ink/80 hover:border-[#0a7653]/40 hover:bg-[#e8fedf]/60 hover:text-[#07533d]",
                )}
              >
                {tag}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Filter Chips Bar */}
      {activeFilterChips.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-muted">Active filters:</span>
          {activeFilterChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => {
                if (chip.clearSearch) setSearchInput("");
                navigateWith(chip.overrides);
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#0a7653]/30 bg-[#e8fedf]/80 px-3 py-1 text-xs font-semibold text-[#07533d] transition hover:bg-[#d5f9c8]"
            >
              <span>{chip.label}</span>
              <X size={13} aria-hidden="true" />
            </button>
          ))}
          <button
            type="button"
            onClick={resetAllFilters}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-muted transition hover:bg-black/5 hover:text-ink"
          >
            <RotateCcw size={12} aria-hidden="true" />
            Reset all
          </button>
        </div>
      )}

      {/* Main 2-Column Workspace: Sticky Facet Sidebar + Job Feed */}
      <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start" ref={resultsTopRef}>
        {/* Desktop Facet Sidebar */}
        <aside
          aria-label="Job filters"
          className="hidden w-[280px] shrink-0 rounded-[18px] border border-[#dfe3e8] bg-white p-5 shadow-[0_4px_16px_rgba(15,23,42,0.03)] lg:sticky lg:top-[108px] lg:block"
        >
          <div className="mb-4 flex items-center justify-between border-b border-[#e5e7eb] pb-3">
            <span
              className={cn(
                companyBrandFont.className,
                "inline-flex items-center gap-2 text-[15px] font-bold tracking-[-0.02em] text-[#14171a]",
              )}
            >
              <SlidersHorizontal size={14} className="text-[#475569]" aria-hidden="true" />
              Refine Roles
            </span>
            {activeFilterChips.length > 0 && (
              <button
                type="button"
                onClick={resetAllFilters}
                className="text-xs font-semibold text-[#475569] hover:text-ink hover:underline"
              >
                Reset all
              </button>
            )}
          </div>
          {renderSidebarControls(false)}
        </aside>

        {/* Right: Results Header + Cards + Pagination */}
        <div className="min-w-0 flex-1">
          {fetchError && (
            <div className="mb-4 flex items-center justify-between rounded-[12px] border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900">
              <span>{fetchError}</span>
              <button
                type="button"
                onClick={() => navigateWith({})}
                className="font-semibold underline hover:text-amber-950"
              >
                Retry
              </button>
            </div>
          )}

          <div className="mb-3.5 flex flex-wrap items-center justify-between gap-2 px-1">
            <p className="text-[13.5px] text-muted" aria-live="polite">
              {totalMatching > 0 ? (
                <>
                  Showing{" "}
                  <span className="font-semibold text-ink">
                    {rangeStart.toLocaleString("en-US")}–{rangeEnd.toLocaleString("en-US")}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-ink">
                    {totalMatching.toLocaleString("en-US")}
                  </span>{" "}
                  active verified QA roles
                </>
              ) : (
                <span>No active QA roles matched your current filter combination</span>
              )}
            </p>

            {isFetching && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green">
                <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                Updating results...
              </span>
            )}
          </div>

          <div
            className={cn(
              "space-y-3.5 transition-opacity duration-150",
              isFetching && "pointer-events-none opacity-60",
            )}
          >
            {jobs.map((job) => (
              <JobCard
                key={job.fingerprint}
                job={job}
                onSelectSkill={(skill) => {
                  setSearchInput(skill);
                  navigateWith({ q: skill, page: 1 }, { scrollToTop: true });
                }}
                onSelectCompany={(company) => {
                  setSearchInput(company);
                  navigateWith({ q: company, page: 1 }, { scrollToTop: true });
                }}
              />
            ))}
          </div>

          {jobs.length === 0 && (
            <div className="rounded-[18px] border border-line bg-white px-6 py-14 text-center shadow-xs">
              <p className="font-display text-xl font-bold text-ink">
                No matching QA openings right now
              </p>
              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted">
                Try broadening your country, removing the experience ceiling, or searching by a core framework such as Playwright, Selenium, or API Testing.
              </p>
              <button
                type="button"
                onClick={resetAllFilters}
                className="mt-5 inline-flex h-10 items-center justify-center gap-2 rounded-[10px] bg-ink px-5 text-sm font-semibold text-white transition hover:bg-[#292929]"
              >
                <RotateCcw size={14} />
                View all {facets.totalActiveJobs.toLocaleString("en-US")} active QA jobs
              </button>
            </div>
          )}

          {/* Crawlable SEO Pagination (intercepted client-side without Next.js _rsc requests) */}
          {totalPages > 1 && (
            <nav
              aria-label="QA jobs pagination"
              className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-line bg-white px-4 py-3 sm:px-5"
            >
              {page > 1 ? (
                <a
                  href={buildJobsHref({ page: page - 1 }, filters)}
                  onClick={(e) => {
                    e.preventDefault();
                    navigateWith({ page: page - 1 }, { scrollToTop: true });
                  }}
                  className="inline-flex h-9 items-center gap-1.5 rounded-[9px] border border-line bg-white px-3 text-xs font-semibold text-ink transition hover:bg-wash"
                >
                  <ChevronLeft size={14} />
                  Previous
                </a>
              ) : (
                <span className="inline-flex h-9 cursor-not-allowed items-center gap-1.5 rounded-[9px] border border-line/60 bg-wash/50 px-3 text-xs font-semibold text-muted/50">
                  <ChevronLeft size={14} />
                  Previous
                </span>
              )}

              <div className="flex flex-wrap items-center gap-1">
                {pageItems.map((item, idx) =>
                  item === "ellipsis" ? (
                    <span
                      key={`ellipsis-${idx}`}
                      className="px-2 text-xs font-semibold text-muted"
                      aria-hidden="true"
                    >
                      …
                    </span>
                  ) : (
                    <a
                      key={item}
                      href={buildJobsHref({ page: item }, filters)}
                      onClick={(e) => {
                        e.preventDefault();
                        if (item !== page) {
                          navigateWith({ page: item }, { scrollToTop: true });
                        }
                      }}
                      aria-current={item === page ? "page" : undefined}
                      className={cn(
                        "inline-flex h-9 min-w-9 items-center justify-center rounded-[9px] px-2.5 text-xs font-bold tabular-nums transition",
                        item === page
                          ? "bg-ink text-lime shadow-xs"
                          : "border border-transparent text-ink/75 hover:border-line hover:bg-wash",
                      )}
                    >
                      {item}
                    </a>
                  ),
                )}
              </div>

              {page < totalPages ? (
                <a
                  href={buildJobsHref({ page: page + 1 }, filters)}
                  onClick={(e) => {
                    e.preventDefault();
                    navigateWith({ page: page + 1 }, { scrollToTop: true });
                  }}
                  className="inline-flex h-9 items-center gap-1.5 rounded-[9px] border border-line bg-white px-3 text-xs font-semibold text-ink transition hover:bg-wash"
                >
                  Next
                  <ChevronRight size={14} />
                </a>
              ) : (
                <span className="inline-flex h-9 cursor-not-allowed items-center gap-1.5 rounded-[9px] border border-line/60 bg-wash/50 px-3 text-xs font-semibold text-muted/50">
                  Next
                  <ChevronRight size={14} />
                </span>
              )}
            </nav>
          )}
        </div>
      </div>

      {/* Popular Crawlable QA Job Hub Links (Internal SEO Silos with direct client navigation) */}
      <section
        aria-labelledby="popular-qa-searches"
        className="mt-14 rounded-[20px] border border-[#dfe3e8] bg-white p-6 shadow-[0_4px_16px_rgba(15,23,42,0.03)] sm:p-8"
      >
        <div className="max-w-2xl">
          <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[.14em] text-[#64748b]">
            <span className="h-3.5 w-[3px] rounded-full bg-[#64748b]" aria-hidden="true" />
            Curated Job Hubs
          </p>
          <h2
            id="popular-qa-searches"
            className={cn(
              companyBrandFont.className,
              "mt-1 text-xl font-bold tracking-[-0.025em] text-[#14171a] sm:text-2xl",
            )}
          >
            Popular QA, SDET & Test Automation Job Searches
          </h2>
          <p className="mt-1.5 text-sm text-[#525b65]">
            Direct career tracks indexed by specialization, location, workplace flexibility, and framework stack.
          </p>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {POPULAR_SEO_SEARCHES.map((hub) => (
            <a
              key={hub.href}
              href={hub.href}
              onClick={(e) => {
                e.preventDefault();
                const url = new URL(hub.href, "http://localhost");
                const sp = url.searchParams;
                navigateWith(
                  {
                    q: sp.get("q") ?? "",
                    country: sp.get("country") ?? "",
                    category: sp.get("category") ?? "",
                    level: sp.get("level") ?? "",
                    workplace: sp.get("workplace") ?? "",
                    maxExp: sp.get("maxExp") ? Number(sp.get("maxExp")) : null,
                    ats: sp.get("ats") ?? "",
                    sort: "newest",
                    page: 1,
                  },
                  { scrollToTop: true },
                );
              }}
              className="group rounded-[14px] border border-[#dfe3e8] bg-[#f8fafc] p-4 transition hover:border-[#94a3b8] hover:bg-white hover:shadow-xs"
            >
              <p
                className={cn(
                  companyBrandFont.className,
                  "text-[15px] font-bold tracking-[-0.015em] text-[#14171a] transition-colors group-hover:text-[#0a7653]",
                )}
              >
                {hub.label}
              </p>
              <p className="mt-1 text-xs leading-5 text-[#525b65]">{hub.subtitle}</p>
            </a>
          ))}
        </div>
      </section>

      {/* Mobile Filter Drawer */}
      {mobileDrawerOpen && (
        <div
          className="fixed inset-0 z-[90] flex justify-end bg-black/50 backdrop-blur-xs lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Refine QA job filters"
        >
          <div className="flex h-full w-full max-w-[360px] flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <span className="font-display text-base font-bold text-ink">Refine QA Jobs</span>
              <button
                type="button"
                onClick={() => setMobileDrawerOpen(false)}
                className="rounded-lg p-1.5 text-muted hover:bg-wash hover:text-ink"
                aria-label="Close filters"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">{renderSidebarControls(true)}</div>
            <div className="flex items-center gap-2.5 border-t border-line p-4">
              <button
                type="button"
                onClick={() => {
                  resetAllFilters();
                  setMobileDrawerOpen(false);
                }}
                className="h-10 flex-1 rounded-[10px] border border-line bg-white text-xs font-semibold text-ink hover:bg-wash"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => setMobileDrawerOpen(false)}
                className="h-10 flex-1 rounded-[10px] bg-ink text-xs font-semibold text-white hover:bg-[#292929]"
              >
                Show {totalMatching.toLocaleString("en-US")} Jobs
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
